import React from 'react';
import clsx from 'clsx';

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
  invalid?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, hint, error, invalid, id, className, children, ...props }, ref) => {
    const autoId = React.useId();
    const fieldId = id ?? autoId;
    const isInvalid = invalid || Boolean(error);
    return (
      <div className={clsx('flex flex-col gap-1.5', className)}>
        {label && (
          <label
            htmlFor={fieldId}
            className="text-[11px] font-medium tracking-wider uppercase text-slate-400"
          >
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={fieldId}
          aria-invalid={isInvalid || undefined}
          aria-describedby={
            error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined
          }
          className={clsx(
            'dark-select w-full bg-surface-2 border rounded-ds-lg text-slate-50 px-3 py-2 text-[13px] outline-none transition-colors duration-150',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            isInvalid
              ? 'border-danger focus:border-danger'
              : 'border-white/[0.08] focus:border-accent',
          )}
          {...props}
        >
          {children}
        </select>
        {error ? (
          <p
            id={`${fieldId}-error`}
            className="text-[11px] text-danger-text"
          >
            {error}
          </p>
        ) : hint ? (
          <p
            id={`${fieldId}-hint`}
            className="text-[11px] text-slate-400"
          >
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
